/**
 * useWhatsAppPresence Hook
 *
 * Gestisce la presenza e typing indicator.
 * - Sottoscrive ai cambi di presenza
 * - Invia typing indicator quando l'utente scrive
 */

import { useEffect, useRef, useCallback } from 'react';
import { useWhatsAppChatStore } from '@/lib/stores/whatsappChatStore';
import { getWahaService } from '@/lib/services/wahaApiService';

interface UseWhatsAppPresenceOptions {
  chatId: string | null;
  text?: string;
}

export function useWhatsAppPresence({
  chatId,
  text = '',
}: UseWhatsAppPresenceOptions) {
  const { wahaConfig, presenceMap, updatePresence, provider } = useWhatsAppChatStore();
  // Numero Ufficiale: "sta scrivendo" via Meta (CloudProvider.setPresence, al
  // massimo ogni 20 s e solo entro 24 ore dall'ultimo messaggio del cliente).
  // MAI il servizio WAHA: potrebbe essere ancora agganciato alla sessione di un
  // altro numero aperto prima.
  const cloudProvider = provider && provider.type !== 'whatsapp' ? provider : null;
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastTypingSentRef = useRef<number>(0);
  const lastSentStateRef = useRef<'typing' | 'paused' | null>(null);

  // DEBUG: Verifica stato wahaConfig
  useEffect(() => {
    console.log('[useWhatsAppPresence] Hook mounted/updated:', {
      chatId,
      hasWahaConfig: !!wahaConfig,
      wahaConfigDetails: wahaConfig ? {
        session: wahaConfig.session,
        apiUrl: wahaConfig.apiUrl?.substring(0, 30) + '...',
      } : null,
    });
  }, [chatId, wahaConfig]);

  // Verifica formato chatId (debug)
  useEffect(() => {
    if (chatId) {
      const hasCorrectFormat = chatId.includes('@c.us') || chatId.includes('@g.us');
      if (!hasCorrectFormat) {
        console.warn('[useWhatsAppPresence] chatId might be in wrong format:', chatId);
      }
    }
  }, [chatId]);

  // Invia typing indicator (debounced)
  const sendTyping = useCallback(async () => {
    if (!chatId || !wahaConfig) {
      console.warn('[useWhatsAppPresence] Cannot send typing - missing config:', {
        chatId: !!chatId,
        wahaConfig: !!wahaConfig,
      });
      return;
    }

    const now = Date.now();
    // Non inviare più di una volta ogni 2 secondi
    if (now - lastTypingSentRef.current < 2000) {
      console.log('[useWhatsAppPresence] Skipping typing - debounced (last sent:', now - lastTypingSentRef.current, 'ms ago)');
      return;
    }

    try {
      if (cloudProvider) {
        await cloudProvider.setPresence(chatId, 'typing');
        lastTypingSentRef.current = now;
        lastSentStateRef.current = 'typing';
        return;
      }
      console.log('[useWhatsAppPresence] Sending typing status:', { chatId });
      const service = getWahaService();
      await service.setPresence(chatId, 'typing');
      console.log('[useWhatsAppPresence] ✅ Typing status sent successfully');
      lastTypingSentRef.current = now;
      lastSentStateRef.current = 'typing';
    } catch (error) {
      console.error('[useWhatsAppPresence] ❌ Error sending typing presence:', {
        chatId,
        error,
        message: error instanceof Error ? error.message : String(error),
      });
    }
  }, [chatId, wahaConfig, cloudProvider]);

  // Invia paused (quando smette di scrivere)
  const sendPaused = useCallback(async () => {
    if (!chatId || !wahaConfig) {
      console.warn('[useWhatsAppPresence] Cannot send paused - missing config:', {
        chatId: !!chatId,
        wahaConfig: !!wahaConfig,
      });
      return;
    }

    // Invia paused solo se prima avevamo inviato typing
    if (lastSentStateRef.current !== 'typing') {
      console.log('[useWhatsAppPresence] Skipping paused - no typing was sent before');
      return;
    }

    // Cloud: nessun "ha smesso di scrivere", l'indicatore scade da solo.
    if (cloudProvider) {
      lastSentStateRef.current = 'paused';
      return;
    }

    try {
      console.log('[useWhatsAppPresence] Sending paused status:', { chatId });
      const service = getWahaService();
      await service.setPresence(chatId, 'paused');
      console.log('[useWhatsAppPresence] ✅ Paused status sent successfully');
      lastSentStateRef.current = 'paused';
    } catch (error) {
      console.error('[useWhatsAppPresence] ❌ Error sending paused presence:', {
        chatId,
        error,
        message: error instanceof Error ? error.message : String(error),
      });
    }
  }, [chatId, wahaConfig, cloudProvider]);

  // Gestione typing basata su cambiamenti del testo
  useEffect(() => {
    const isTyping = text.trim().length > 0;

    if (isTyping) {
      // User sta digitando (text ha contenuto)
      sendTyping(); // Debounced a 2s

      // Resetta timeout per paused
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      // Imposta timeout per inviare paused dopo 3s di inattività
      typingTimeoutRef.current = setTimeout(() => {
        sendPaused();
      }, 3000);
    } else {
      // Text è vuoto
      // Invia paused solo se prima avevamo inviato typing
      if (lastSentStateRef.current === 'typing') {
        sendPaused();
      }

      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    }

    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [text, sendTyping, sendPaused]);

  // Sottoscrivi alla presenza del contatto quando si seleziona una chat
  useEffect(() => {
    // Cloud non espone la presenza del cliente: niente sottoscrizione WAHA.
    if (!chatId || !wahaConfig || cloudProvider) return;

    let isSubscribed = false;

    const subscribeToPresence = async () => {
      try {
        console.log('[Presence] Subscribing to presence for:', chatId);
        const service = getWahaService();
        await service.subscribePresence(chatId);
        isSubscribed = true;
        console.log('[Presence] Successfully subscribed to presence for:', chatId);

        // Ottieni presenza iniziale
        const presence = await service.getPresence(chatId);

        // Converti il formato da WAHA API a ContactPresence
        const status = presence.isTyping ? 'typing'
                     : presence.isOnline ? 'online'
                     : 'offline';

        updatePresence(chatId, {
          chatId,
          status,
          lastSeen: presence.lastSeen ? new Date(presence.lastSeen) : undefined,
        });

        console.log('[Presence] Initial presence state:', { chatId, status });
      } catch (error) {
        console.error('[Presence] Failed to subscribe to presence:', error);
      }
    };

    subscribeToPresence();

    return () => {
      // Cleanup
      if (isSubscribed) {
        console.log('[Presence] Cleanup subscription for:', chatId);
      }
    };
  }, [chatId, wahaConfig, updatePresence, cloudProvider]);

  // Ritorna la presenza corrente
  const currentPresence = chatId ? presenceMap.get(chatId) : undefined;

  return {
    isOnline: currentPresence?.status === 'online',
    isTyping: currentPresence?.status === 'typing',
    lastSeen: currentPresence?.lastSeen,
    sendTyping,
    sendPaused,
  };
}
